/**
 * Step 5, the last step. `Continue` and `Skip` run one shared finish path:
 * write the fan's selected leagues to /follows/set, then mark onboarding
 * done. The sport ids field on that write is always the empty array, per
 * owner answer Q285: no sport-shaped follow is ever stored.
 */
import {
  useFollowsSet,
  useProfileGet,
  useProfileUpdate,
} from "@sportsmet/profile-sdk/private";
import { useLeagueCatalogGet } from "@sportsmet/sports-sdk/private";
import { Button, Skeleton } from "@sportsmet/ui";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useEffect, useRef, useState } from "react";

import { extractErrorCode, showApiError } from "../../errors/apiErrorBus";
import { LeagueToggleGroups } from "../follows/LeagueToggleGroups";
import {
  NO_LEAGUES_BODY,
  NO_LEAGUES_TITLE,
} from "../scores/leagueSelectorContract";
import { togglePinnedLeague } from "../scores/pinnedLeagues";
import {
  CONTINUE_LABEL,
  CONTINUE_PENDING_LABEL,
  LEAGUES_GROUP_HELP,
  LEAGUES_GROUP_LABEL_SUFFIX,
  LEAGUES_HEADLINE,
  LEAGUES_SUB,
  ONBOARDING_SKIP_LABEL,
} from "./onboardingCopy";
import {
  buildLeagueGroups,
  resolveInitialLeagueIds,
} from "./onboardingFollowsModel";
import { OnboardingStepFrame } from "./OnboardingStepFrame";
import { writeProfileThrough } from "./profileCacheWriteThrough";

export const LeaguesStep = ({
  stepIndex,
  stepTotal,
  selectedSportIds,
  onFinished,
}: {
  /** This fan's position and total, per Q274. Passed straight to OnboardingStepFrame. */
  stepIndex: number;
  stepTotal: number;
  /** The sports staged on the previous step. Empty when the fan picked none,
   *  or when they landed here directly and the staged selection is gone. */
  selectedSportIds: string[];
  /**
   * Called after /follows/set and /profile/update have BOTH succeeded. The
   * route navigates to /scores. This component never navigates.
   */
  onFinished: () => void;
}): ReactNode => {
  const catalog = useLeagueCatalogGet({});
  const profileQuery = useProfileGet({});
  const follows = useFollowsSet();
  const update = useProfileUpdate();
  const queryClient = useQueryClient();
  const [selection, setSelection] = useState<string[] | null>(null);
  const isSaving = useRef(false);

  useEffect(() => {
    const code = extractErrorCode(catalog.data);
    if (code !== null)
      showApiError(catalog.data, undefined, catalog.dataUpdatedAt);
  }, [catalog.dataUpdatedAt]);

  useEffect(() => {
    const code = extractErrorCode(profileQuery.data);
    if (code !== null)
      showApiError(profileQuery.data, undefined, profileQuery.dataUpdatedAt);
  }, [profileQuery.dataUpdatedAt]);

  const sports = catalog.data?.data?.sports ?? [];
  const followedLeagueIds =
    profileQuery.data?.data?.profile?.followedLeagueIds ?? [];

  useEffect(() => {
    if (selection !== null) return;
    if (catalog.isPending || profileQuery.isPending) return;
    setSelection(
      resolveInitialLeagueIds({ sports, selectedSportIds, followedLeagueIds }),
    );
  }, [selection, catalog.isPending, profileQuery.isPending]);

  const selectedLeagueIds = selection ?? [];
  const groups = buildLeagueGroups({ sports, selectedSportIds });

  function toggleLeague(leagueId: string): void {
    setSelection(
      togglePinnedLeague({ pinnedLeagueIds: selectedLeagueIds, leagueId }),
    );
  }

  async function finish(leagueIds: string[]): Promise<void> {
    if (isSaving.current) return;
    isSaving.current = true;

    let followsResult;
    try {
      followsResult = await follows.mutateAsync({
        followedSportIds: [],
        followedLeagueIds: leagueIds,
      });
    } catch {
      showApiError({ error: { code: "INTERNAL_CALL_FAILED", message: "" } });
      isSaving.current = false;
      return;
    }

    if (showApiError(followsResult)) {
      isSaving.current = false;
      return;
    }

    let updateResult;
    try {
      updateResult = await update.mutateAsync({ onboardingStep: "done" });
    } catch {
      showApiError({ error: { code: "INTERNAL_CALL_FAILED", message: "" } });
      isSaving.current = false;
      return;
    }

    if (showApiError(updateResult)) {
      isSaving.current = false;
      return;
    }

    writeProfileThrough({ queryClient, response: updateResult });

    onFinished();
  }

  const isPending = follows.isPending || update.isPending;

  return (
    <OnboardingStepFrame
      stepIndex={stepIndex}
      stepTotal={stepTotal}
      headline={LEAGUES_HEADLINE}
      sub={LEAGUES_SUB}
      isWide
    >
      {catalog.isPending || profileQuery.isPending ? (
        <div data-testid="onboarding-leagues-loading">
          <Skeleton shape="text" />
          <Skeleton shape="text" />
          <Skeleton shape="text" />
        </div>
      ) : groups.length === 0 ? (
        <div data-testid="onboarding-leagues-empty">
          <h2 className="text-body-strong text-ink">{NO_LEAGUES_TITLE}</h2>
          <p className="text-body text-ink-secondary">{NO_LEAGUES_BODY}</p>
        </div>
      ) : (
        <LeagueToggleGroups
          groups={groups}
          selectedLeagueIds={selectedLeagueIds}
          onToggle={toggleLeague}
          testIdPrefix="onboarding-league-card-"
          groupLabelSuffix={LEAGUES_GROUP_LABEL_SUFFIX}
          groupHelp={LEAGUES_GROUP_HELP}
        />
      )}
      <div className="sm-screen-bottom pt-2">
        <div className="flex gap-3">
          <Button
            variant="secondary"
            className="flex-1"
            disabled={false}
            data-testid="onboarding-leagues-skip"
            onClick={() => {
              void finish([]);
            }}
          >
            {ONBOARDING_SKIP_LABEL}
          </Button>
          <Button
            className="flex-1"
            disabled={false}
            data-testid="onboarding-leagues-continue"
            onClick={() => {
              void finish(selectedLeagueIds);
            }}
          >
            {isPending ? CONTINUE_PENDING_LABEL : CONTINUE_LABEL}
          </Button>
        </div>
      </div>
    </OnboardingStepFrame>
  );
};
